import { CustomProductListState } from "@iewcasia/Store/State/ProductList/ProductListState";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { ProductInventoryDto, ProductModel, RealTimeInventoryModel, RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

const initialState: CustomProductListState = {
    page: 1,
    pageSize: 10,
    scrollPosition: 0,
    isLoading: false,
    products: [],
    productInfosByProductId: {},
};

const customProductListReducer = {
    "Custom/ProductList/InfiniteScrollLoading": (draft: Draft<CustomProductListState>) => ({
        ...draft,
        isLoading: true,
    }),

    "Custom/ProductList/InfiniteScrollLoaded": (draft: Draft<CustomProductListState>) => ({
        ...draft,
        isLoading: false,
    }),

    "Custom/ProductList/SetPage": (draft: Draft<CustomProductListState>, action: { page: number }) => ({
        ...draft,
        page: action.page,
    }),

    // replace=true: sort/filter/page-1 load. replace=false: infinite-scroll append.
    // Always merge productInfos so existing pricing/inventory on earlier pages is kept when appending.
    "Custom/ProductList/SetProducts": (
        draft: Draft<CustomProductListState>,
        action: {
            products: ProductModel[];
            productInfosByProductId: SafeDictionary<ProductInfo>;
            replace?: boolean;
        },
    ) => {
        const incomingProducts = action.products ?? [];
        const incomingInfos = action.productInfosByProductId ?? {};

        if (action.replace) {
            draft.products = incomingProducts;
            draft.productInfosByProductId = incomingInfos;
            draft.page = 1;
            return;
        }

        const existingIds = new Set((draft.products ?? []).map(product => product.id));
        const productsToAppend = incomingProducts.filter(product => !existingIds.has(product.id));
        draft.products = [...(draft.products ?? []), ...productsToAppend];
        // Incoming infos from SetUpProductInfos already include prior pages' pricing/inventory when appending.
        draft.productInfosByProductId = {
            ...draft.productInfosByProductId,
            ...incomingInfos,
        };
    },
    "Custom/ProductList/ResetInfiniteScrollPage": (draft: Draft<CustomProductListState>) => {
        draft.page = 1;
        draft.products = [];
        draft.productInfosByProductId = {};
    },

    "Custom/ProductList/InfiniteScroll": (
        draft: Draft<CustomProductListState>,
        action: {
            page: number;
            pageSize: number;
            scrollPosition: number;
        },
    ) => {
        draft.page = action.page;
        draft.pageSize = action.pageSize ?? 10;
        draft.scrollPosition = action.scrollPosition;
    },

    "Custom/ProductList/SetFilterLoading": (
        draft: Draft<CustomProductListState>,
        action: {
            isLoadingFilter: boolean;
        },
    ) => {
        draft.isLoadingFilter = action.isLoadingFilter;
    },
    
    "Custom/ProductList/CompleteLoadRealTimePricing": (
        draft: Draft<CustomProductListState>,
        action: { realTimePricing: RealTimePricingModel },
    ) => {
        action.realTimePricing.realTimePricingResults?.forEach(pricing => {
            const productInfo = draft.productInfosByProductId?.[pricing.productId];
            if (productInfo) {
                productInfo.pricing = pricing;
                delete productInfo.failedToLoadPricing;
            }
        });
    },
    "Custom/ProductList/FailedLoadRealTimePricing": (draft: Draft<CustomProductListState>, action: { productId?: string }) => {
        if (action.productId) {
            const productInfo = draft.productInfosByProductId?.[action.productId];
            if (productInfo) {
                productInfo.failedToLoadPricing = true;
            }
            return;
        }

        for (const productId in draft.productInfosByProductId) {
            draft.productInfosByProductId[productId]!.failedToLoadPricing = true;
        }
    },

    "Custom/ProductList/CompleteLoadRealTimeInventory": (
        draft: Draft<CustomProductListState>,
        action: { realTimeInventory: RealTimeInventoryModel },
    ) => {
        action.realTimeInventory?.realTimeInventoryResults?.forEach((inventory: ProductInventoryDto) => {
            const productInfo = draft.productInfosByProductId?.[inventory.productId];
            if (productInfo) {
                productInfo.inventory = inventory;
            }
        });
    },
    "Custom/ProductList/FailedLoadRealTimeInventory": (
        draft: Draft<CustomProductListState>,
        action: { productId?: string },
    ) => {
        if (action.productId) {
            const productInfo = draft.productInfosByProductId?.[action.productId];
            if (productInfo) {
                productInfo.failedToLoadInventory = true;
            }
            return;
        }

        for (const productId in draft.productInfosByProductId) {
            draft.productInfosByProductId[productId]!.failedToLoadInventory = true;
        }
    },
};

export default createTypedReducerWithImmer(initialState, customProductListReducer);
